import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Paper,
  IconButton,
  TextField,
  useTheme,
  Divider,
  Avatar,
  Fade,
  AppBar,
  Toolbar,
} from "@mui/material";
import {
  TrendingUp,
  School,
  Timer,
  ArrowForward,
  KeyboardArrowUp,
  Mail,
  Brightness4,
  Brightness7,
} from "@mui/icons-material";
import { useColorMode } from "../../../theme/ThemeContext";
import axios from "axios";
import Swal from "sweetalert2";
import { motion } from "framer-motion";

// Components
import Footer from "../Components/Footer";
import LogosSection from "../Components/LogosSection";

// Images
import KaasLogo from "../Images/Vetrina _Logo.png";
import KaasWhiteLogo from "../Styles/KaasWhite.png";
import HeroBg from "../Images/b2efdca3-f62c-4ee8-91ea-c5710a5d4dc8.jpg";
import wwdImg from "../Images/landing_GroupImage.jpg";
import analyticsIcon from "../Images/analytics.jpg";
import teacherIcon from "../Images/teacher.jpg";
import periodIcon from "../Images/period.jpg";

const LandingPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { mode, toggleColorMode } = useColorMode();
  const [popularCourses, setPopularCourses] = useState([]);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [email, setEmail] = useState("");

  const API_URL = "https://tdtlworld.com/dss-kaas-backend/";

  useEffect(() => {
    axios.get(`${API_URL}lms/popular/`).then((response) => {
      setPopularCourses(response.data.popular_courses || []);
    });

    const handleScroll = () => setShowBackToTop(window.scrollY > 300);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email) {
      Swal.fire({
        title: "Success!",
        text: "Thank You For Subscribing!",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });
      setEmail("");
    }
  };

  const featureItems = [
    {
      icon: <TrendingUp sx={{ fontSize: 40 }} />,
      title: "Upgrading Skills",
      text: "Upskill yourself and prepare for professional progress with our certified courses.",
      image: analyticsIcon,
    },
    {
      icon: <School sx={{ fontSize: 40 }} />,
      title: "Expert Instructors",
      text: "Courses are created and taught by leading experts with real-world industry experience.",
      image: teacherIcon,
    },
    {
      icon: <Timer sx={{ fontSize: 40 }} />,
      title: "Learn Anywhere",
      text: "Learn at your own speed and in the comfort of your own home, anytime you want.",
      image: periodIcon,
    },
  ];

  return (
    <Box
      sx={{
        bgcolor: "background.default",
        color: "text.primary",
        overflowX: "hidden",
      }}
    >
      {/* Navbar for Landing */}
      <AppBar
        position="sticky"
        sx={{
          bgcolor: "background.paper",
          boxShadow: "none",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Container maxWidth="lg">
          <Toolbar sx={{ justifyContent: "space-between", py: 1 }}>
            <Box
              component={Link}
              to="/db-shiksha/kaas"
              sx={{ display: "flex", alignItems: "center" }}
            >
              <img
                src={mode === "dark" ? KaasWhiteLogo : KaasLogo}
                alt="Logo"
                style={{
                  height: "40px",
                  width: "auto",
                  objectFit: "contain",
                }}
              />
            </Box>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <IconButton
                onClick={toggleColorMode}
                sx={{ color: mode === "dark" ? "#D4AF37" : "#0D0D0D" }}
              >
                {mode === "dark" ? <Brightness7 /> : <Brightness4 />}
              </IconButton>
              <Button
                component={Link}
                to="/db-shiksha/kaas/login"
                variant="text"
                sx={{
                  fontWeight: 700,
                  color: mode === "dark" ? "#FFFFFF" : "#0D0D0D",
                }}
              >
                Sign In
              </Button>
              <Button
                component={Link}
                to="/db-shiksha/kaas/login"
                variant="contained"
                sx={{ fontWeight: 700, borderRadius: "50px" }}
              >
                Sign Up
              </Button>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Hero Section */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <Typography
                  variant="h2"
                  fontWeight={800}
                  gutterBottom
                  sx={{ lineHeight: 1.2, color: "text.primary" }}
                >
                  Up-Skill As{" "}
                  <Box component="span" sx={{ color: "#D4AF37" }}>
                    You Work
                  </Box>
                </Typography>
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{ mb: 3, color: "text.secondary" }}
                >
                  Learning Anywhere, Anytime.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "text.secondary", mb: 4, maxWidth: "90%" }}
                >
                  Welcome to KaaS, a personalized learning platform designed for
                  students and companies alike. Explore courses in cutting-edge
                  technologies, tailor your learning journey, and empower
                  yourself with expert-led training.
                </Typography>
                <Button
                  component={Link}
                  to="/db-shiksha/kaas/login"
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForward />}
                  sx={{ borderRadius: "50px", px: 4, py: 1.5, fontWeight: 700 }}
                >
                  Get Started
                </Button>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <Box
                  component="img"
                  src={HeroBg}
                  alt="Learning"
                  sx={{
                    width: "100%",
                    borderRadius: "24px",
                    boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                />
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* What we do */}
      <Box sx={{ py: 10, bgcolor: "background.paper" }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              p: { xs: 4, md: 8 },
              borderRadius: "32px",
              background:
                theme.palette.mode === "dark"
                  ? "linear-gradient(135deg, #1A1A1A 0%, #0D0D0D 100%)"
                  : "linear-gradient(135deg, #F9F9F9 0%, #FFFFFF 100%)",
              border: "1px solid",
              borderColor: "divider",
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: "center",
              gap: 6,
            }}
          >
            <Box
              component="img"
              src={wwdImg}
              alt="What we do"
              sx={{
                width: { xs: "200px", md: "300px" },
                height: { xs: "200px", md: "300px" },
                borderRadius: "50%",
                border: "8px solid #D4AF37",
                objectFit: "cover",
              }}
            />
            <Box>
              <Typography variant="h3" fontWeight={800} gutterBottom>
                What We Do
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "text.secondary", lineHeight: 1.8 }}
              >
                KaaS is a prominent one-stop solution for all your learning
                needs. The courses have been designed and developed from the
                learner perspective. With this software, you may learn the
                skills you need to advance professionally and personally. Master
                the skills to grow professionally and personally with this app.
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Features */}
      <Box sx={{ py: 12 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {featureItems.map((item, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    height: "100%",
                    textAlign: "center",
                    borderRadius: "24px",
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                    "&:hover": {
                      borderColor: "#D4AF37",
                      transform: "translateY(-10px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  <Box sx={{ color: "#D4AF37", mb: 3 }}>{item.icon}</Box>
                  <Typography variant="h5" fontWeight={700} gutterBottom>
                    {item.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {item.text}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Numbers Section */}
      <Box sx={{ py: 10, bgcolor: "background.paper" }}>
        <Container maxWidth="lg">
          <Grid
            container
            spacing={4}
            justifyContent="center"
            textAlign="center"
          >
            {[
              { label: "Courses", val: "200+" },
              { label: "Industry Experts", val: "200+" },
              { label: "Trained Professional", val: "5000+" },
              { label: "Top Ranked Programs", val: "200+" },
            ].map((stat, i) => (
              <Grid item xs={6} md={3} key={i}>
                <Typography
                  variant="h3"
                  fontWeight={800}
                  sx={{ color: "#D4AF37" }}
                >
                  {stat.val}
                </Typography>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  color="text.secondary"
                >
                  {stat.label}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Logos Section */}
      <Box sx={{ py: 10 }}>
        <Container maxWidth="lg">
          <LogosSection />
        </Container>
      </Box>

      {/* CTA / Subscribe */}
      <Box sx={{ py: 12, bgcolor: "background.paper" }}>
        <Container maxWidth="md" sx={{ textAlign: "center" }}>
          <Typography variant="h3" fontWeight={800} gutterBottom>
            Are You Ready To Upgrade?
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 6 }}>
            Join the journey towards an upskilled future by reskilling yourself
            with KaaS.
          </Typography>

          <Box
            component="form"
            onSubmit={handleSubscribe}
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "center",
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <TextField
              placeholder="Enter Your Email Address..."
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              sx={{
                width: { xs: "100%", sm: "400px" },
                "& .MuiOutlinedInput-root": { borderRadius: "50px" },
              }}
            />
            <Button
              type="submit"
              variant="contained"
              size="large"
              sx={{ borderRadius: "50px", px: 6, fontWeight: 700 }}
            >
              Subscribe
            </Button>
          </Box>
        </Container>
      </Box>

      <Footer />

      {showBackToTop && (
        <IconButton
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          sx={{
            position: "fixed",
            bottom: 30,
            right: 30,
            bgcolor: "#D4AF37",
            color: "black",
            "&:hover": { bgcolor: "#B8860B" },
          }}
        >
          <KeyboardArrowUp />
        </IconButton>
      )}
    </Box>
  );
};

export default LandingPage;
